import { DossierVosOproep, VosOproep } from "@/entities/medewerkers-op-station";

import { Badge, BadgeVariant, DataTable, DataTableColumnHeader } from "@/shared/components";
import type { DataTableFeatures } from "@/shared/components/table/data-table-features";
import { DataTableToolbarOptions } from "@/shared/components/table/data-table-toolbar";
import { formatDateTimeRelative } from "@/shared/utils";
import { createColumnHelper } from "@tanstack/react-table";

type VosOproepRow = VosOproep & {
    dossierId: number;
    omschrijving: string;
};

type VosOproepenTableProps = {
    dossierVosOproepen: DossierVosOproep[];
};

function flattenOproepen(dossierVosOproepen: DossierVosOproep[]): VosOproepRow[] {
    return dossierVosOproepen
        .flatMap((dossier) =>
            Object.values(dossier.vosOproepen)
                .flat()
                .map((oproep) => ({
                    ...oproep,
                    dossierId: dossier.dossierId,
                    omschrijving: dossier.omschrijving,
                }))
        )
        .sort(VosOproep.sort);
}

const statusVariant: Record<VosOproep["status"], BadgeVariant> = {
    Aangevraagd: "blue-subtle",
    Komt: "green-subtle",
    KomtNiet: "red-subtle",
    NietMeerNodig: "gray-subtle",
    NietOproepbaar: "yellow-subtle",
    Gestopt: "red-subtle",
    AanvraagVerlopen: "orange-subtle",
};

const statusLabel: Record<VosOproep["status"], string> = {
    Aangevraagd: "Aangevraagd",
    Komt: "Komt",
    KomtNiet: "Komt niet",
    NietMeerNodig: "Niet meer nodig",
    NietOproepbaar: "Niet oproepbaar",
    Gestopt: "Gestopt",
    AanvraagVerlopen: "Aanvraag verlopen",
};

export function VosOproepenTable({ dossierVosOproepen }: Readonly<VosOproepenTableProps>) {
    const rows = flattenOproepen(dossierVosOproepen);

    const columnHelper = createColumnHelper<DataTableFeatures, VosOproepRow>();

    const columns = columnHelper.columns([
        columnHelper.accessor("dossierId", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Dossier" />,
        }),
        columnHelper.accessor("omschrijving", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Omschrijving" />,
        }),
        columnHelper.accessor("entraId", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Entra ID" />,
        }),
        columnHelper.accessor("opStation", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Station" />,
        }),
        columnHelper.accessor("status", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
            cell: ({ row }) => <Badge variant={statusVariant[row.original.status]}>{statusLabel[row.original.status]}</Badge>,
        }),
        columnHelper.accessor("geldigTot", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Geldig tot" />,
            cell: ({ row }) => formatDateTimeRelative(new Date(row.original.geldigTot)),
        }),
        columnHelper.display({
            id: "url",
            header: ({ column }) => <DataTableColumnHeader column={column} title="URL" />,
            cell: ({ row }) => {
                const url = `${window.location.origin}/vos-registratie?uniqueToken=${row.original.id}`;
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-content-link underline">
                        {url}
                    </a>
                );
            },
        }),
    ]);

    const toolbarOptions: DataTableToolbarOptions<VosOproepRow> = {
        filters: {
            globalFilter: {
                columns: ["dossierId", "omschrijving", "entraId", "opStation", "status"],
                placeholder: "Zoek op dossier, station of status...",
            },
        },
    };

    return <DataTable columns={columns} data={rows} toolbarOptions={toolbarOptions} />;
}
